<template>
  <div class="page pay-orders-page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>充值记录</span>
        <span class="hint">账号充值 / 会员购买流水；支付超过 7 天不可退款</span>
      </div>

      <el-form :inline="true" size="small" class="filter-form">
        <el-form-item label="开始日期">
          <el-date-picker
            v-model="filters.startDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="开始日期"
            :clearable="false"
          />
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker
            v-model="filters.endDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="结束日期"
            :clearable="false"
          />
        </el-form-item>
        <el-form-item label="订单类型">
          <el-select v-model="filters.orderType" clearable placeholder="全部类型" style="width: 160px">
            <el-option v-for="opt in typeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付状态">
          <el-select v-model="filters.status" clearable placeholder="全部" style="width: 120px">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadData">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="stats-row">
        <div class="stat-card stat-total">
          <div class="stat-label">已支付总额</div>
          <div class="stat-value">¥{{ formatMoney(summary.totalPaidAmount) }}</div>
          <div class="stat-sub">{{ summary.totalPaidCount }} 笔</div>
        </div>
        <div v-for="item in summary.stats" :key="item.orderType" class="stat-card">
          <div class="stat-label">{{ displayOrderTypeLabel(item.orderType, item.orderTypeLabel) }}</div>
          <div class="stat-value">¥{{ formatMoney(item.paidAmount) }}</div>
          <div class="stat-sub">{{ item.paidCount }} 笔 / 共 {{ item.orderCount }} 单</div>
        </div>
      </div>

      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column label="充值账号" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.userName || '-' }}
            <span v-if="row.userId != null" class="text-muted"> · ID:{{ row.userId }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="userPhone" label="充值手机" width="120" />
        <el-table-column label="充值平台" width="90">
          <template slot-scope="{ row }">{{ row.paymentMethodLabel || row.channel || '-' }}</template>
        </el-table-column>
        <el-table-column prop="orderNo" label="充值单号" min-width="170" show-overflow-tooltip />
        <el-table-column label="充值时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.paidAt || row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="充值金额" width="110">
          <template slot-scope="{ row }">¥{{ formatMoney(row.amount) }}</template>
        </el-table-column>
        <el-table-column label="对应积分" width="100" align="right">
          <template slot-scope="{ row }">{{ formatPoints(row) }}</template>
        </el-table-column>
        <el-table-column label="购买项目" width="120">
          <template slot-scope="{ row }">
            {{ displayOrderTypeLabel(row.orderType, row.orderTypeLabel) }}
          </template>
        </el-table-column>
        <el-table-column label="明细" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.purchaseDetail || row.productTitle || '-' }}</template>
        </el-table-column>
        <el-table-column label="支付账号" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.payAccount || row.thirdTradeNo || '-' }}</template>
        </el-table-column>
        <el-table-column prop="statusLabel" label="状态" width="90" />
        <el-table-column label="退款" width="100">
          <template slot-scope="{ row }">
            <span v-if="Number(row.refundAmount || 0) > 0" class="refund-amount">
              ¥{{ formatMoney(row.refundAmount) }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="canRefund(row)"
              type="text"
              class="refund-btn"
              @click="openRefund(row)"
            >
              退款
            </el-button>
            <span v-else-if="isRefundExpired(row)" class="refund-expired">超期不可退</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager-wrap">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </el-card>

    <el-dialog
      title="订单退款"
      :visible.sync="refundVisible"
      width="500px"
      custom-class="refund-dialog"
      @closed="resetRefundForm"
    >
      <div v-if="refundTarget" class="refund-dialog-body">
        <div class="refund-summary">
          <div class="refund-summary-row">
            <span class="refund-summary-label">支付单号</span>
            <span class="refund-summary-value mono">{{ refundTarget.orderNo }}</span>
          </div>
          <div class="refund-summary-grid">
            <div class="refund-summary-item">
              <span class="refund-summary-label">订单金额</span>
              <span class="refund-summary-value">¥{{ formatMoney(refundTarget.amount) }}</span>
            </div>
            <div class="refund-summary-item">
              <span class="refund-summary-label">已退金额</span>
              <span class="refund-summary-value refund-paid">¥{{ formatMoney(refundTarget.refundAmount) }}</span>
            </div>
            <div class="refund-summary-item refund-summary-item--full">
              <span class="refund-summary-label">可退金额</span>
              <span class="refund-summary-value refund-max">¥{{ formatMoney(refundableAmount(refundTarget)) }}</span>
            </div>
          </div>
        </div>

        <div v-if="refundTarget.refundLogs && refundTarget.refundLogs.length" class="refund-history">
          <div class="refund-history-title">历史退款记录</div>
          <div
            v-for="(log, idx) in refundTarget.refundLogs"
            :key="log.id || idx"
            class="refund-history-item"
          >
            <span class="refund-history-amount">¥{{ formatMoney(log.amount) }}</span>
            <span class="refund-history-remark">{{ log.remark || '（无备注）' }}</span>
            <span v-if="log.createdAt" class="refund-history-time">{{ formatTime(log.createdAt) }}</span>
          </div>
        </div>

        <el-form ref="refundForm" :model="refundForm" :rules="refundRules" label-width="108px" size="small" class="refund-form">
          <el-form-item label="退款金额" prop="amount">
            <el-input v-model="refundForm.amount" placeholder="请输入本次退款金额">
              <template slot="append">元</template>
            </el-input>
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="refundForm.remark" type="textarea" :rows="2" placeholder="可选，如：误购年付部分退款" />
          </el-form-item>
          <el-form-item label="管理员密码" prop="adminPassword">
            <el-input
              v-model="refundForm.adminPassword"
              type="password"
              show-password
              placeholder="请输入登录密码"
              autocomplete="new-password"
            />
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="refund-dialog-footer">
        <el-button @click="refundVisible = false">取消</el-button>
        <el-button type="danger" :loading="refundSubmitting" @click="submitRefund">确认退款</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPayOrders, refundPayOrder } from '@/api/admin'

const REFUND_WINDOW_DAYS = 7

function pad(n) {
  return String(n).padStart(2, '0')
}

function todayStr() {
  const t = new Date()
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`
}

function monthStartStr() {
  const t = new Date()
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-01`
}

function parsePaidDate(paidAt) {
  if (!paidAt) return null
  const raw = String(paidAt).trim()
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const d = new Date(normalized)
  return Number.isNaN(d.getTime()) ? null : d
}

function daysSincePaid(paidAt) {
  const paid = parsePaidDate(paidAt)
  if (!paid) return Number.POSITIVE_INFINITY
  const paidDay = new Date(paid.getFullYear(), paid.getMonth(), paid.getDate())
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.floor((today - paidDay) / (24 * 60 * 60 * 1000))
}

function isWithinRefundWindow(paidAt) {
  return daysSincePaid(paidAt) <= REFUND_WINDOW_DAYS
}

export default {
  name: 'AdminPayOrders',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      summary: { stats: [], totalPaidAmount: 0, totalPaidCount: 0 },
      filters: {
        startDate: monthStartStr(),
        endDate: todayStr(),
        orderType: '',
        status: 1
      },
      typeOptions: [
        { value: 'point', label: '积分购买' },
        { value: 'membership', label: '会员充值' },
        { value: 'eco_point', label: '生态积分充值' },
        { value: 'promotion', label: '流量包购买' },
        { value: 'training_class', label: '智享课堂购买' },
        { value: 'training_material', label: '培训材料购买' }
      ],
      orderTypeLabelMap: {
        point: '积分购买',
        membership: '会员充值',
        eco_point: '生态积分充值',
        promotion: '流量包购买',
        training_class: '智享课堂购买',
        training_material: '培训材料购买',
        training: '课程购买'
      },
      statusOptions: [
        { value: 1, label: '已支付' },
        { value: 0, label: '待支付' },
        { value: 2, label: '已退款' }
      ],
      refundVisible: false,
      refundSubmitting: false,
      refundTarget: null,
      refundForm: { amount: '', remark: '', adminPassword: '' }
    }
  },
  computed: {
    refundRules() {
      return {
        amount: [{ required: true, validator: this.validateRefundAmount, trigger: 'blur' }],
        adminPassword: [{ required: true, message: '请输入管理员密码', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatMoney(v) {
      return Number(v || 0).toFixed(2)
    },
    formatPoints(row) {
      if (row && row.points != null && row.points !== '') {
        return Number(row.points).toLocaleString('zh-CN')
      }
      const detail = String((row && row.purchaseDetail) || '')
      const m = detail.match(/(\d[\d,]*)/)
      return m ? m[1] : '-'
    },
    displayOrderTypeLabel(orderType, fallback) {
      const mapped = this.orderTypeLabelMap[orderType]
      if (mapped) return mapped
      // 避免把 biz_type 英文码直接露出
      if (fallback && !/^[a-z][a-z0-9_]*$/i.test(String(fallback))) return fallback
      return this.orderTypeLabelMap[fallback] || fallback || '-'
    },
    formatTime(v) {
      return v ? String(v).replace('T', ' ').slice(0, 19) : '-'
    },
    refundableAmount(row) {
      if (!row) return 0
      return Math.max(0, Number(row.amount || 0) - Number(row.refundAmount || 0))
    },
    canRefund(row) {
      return Number(row.status) === 1
        && this.refundableAmount(row) > 0
        && isWithinRefundWindow(row.paidAt)
    },
    isRefundExpired(row) {
      return Number(row.status) === 1
        && this.refundableAmount(row) > 0
        && !isWithinRefundWindow(row.paidAt)
    },
    validateRefundAmount(rule, value, callback) {
      const amount = Number(value)
      if (!value || Number.isNaN(amount) || amount <= 0) {
        callback(new Error('请输入大于 0 的退款金额'))
        return
      }
      const max = this.refundableAmount(this.refundTarget)
      if (amount - max > 0.0001) {
        callback(new Error(`退款金额不能超过可退金额 ¥${this.formatMoney(max)}`))
        return
      }
      callback()
    },
    resetFilters() {
      this.filters = {
        startDate: monthStartStr(),
        endDate: todayStr(),
        orderType: '',
        status: 1
      }
      this.page = 1
      this.loadData()
    },
    onPageChange(p) {
      this.page = p
      this.loadData()
    },
    openRefund(row) {
      if (!this.canRefund(row)) {
        if (this.isRefundExpired(row)) {
          this.$message.warning(`订单支付已超过 ${REFUND_WINDOW_DAYS} 天，不允许退款`)
        }
        return
      }
      this.refundTarget = row
      this.refundForm = {
        amount: this.formatMoney(this.refundableAmount(row)),
        remark: '',
        adminPassword: ''
      }
      this.refundVisible = true
      this.$nextTick(() => {
        if (this.$refs.refundForm) this.$refs.refundForm.clearValidate()
      })
    },
    resetRefundForm() {
      this.refundTarget = null
      this.refundForm = { amount: '', remark: '', adminPassword: '' }
    },
    submitRefund() {
      if (!this.refundTarget) return
      this.$refs.refundForm.validate(async valid => {
        if (!valid) return
        this.refundSubmitting = true
        try {
          await refundPayOrder(this.refundTarget.orderNo, {
            amount: Number(this.refundForm.amount),
            remark: this.refundForm.remark || undefined,
            adminPassword: this.refundForm.adminPassword
          })
          this.$message.success('退款成功')
          this.refundVisible = false
          this.loadData()
        } catch (e) {
          this.$message.error(e.message || '退款失败')
        } finally {
          this.refundSubmitting = false
        }
      })
    },
    async loadData() {
      this.loading = true
      try {
        const data = await getPayOrders({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          orderType: this.filters.orderType || undefined,
          status: this.filters.status === '' || this.filters.status == null ? undefined : this.filters.status,
          page: this.page,
          pageSize: this.pageSize
        })
        this.list = data.list || []
        this.total = Number(data.total || 0)
        this.summary = {
          stats: data.stats || [],
          totalPaidAmount: data.totalPaidAmount || 0,
          totalPaidCount: (data.stats || []).reduce((sum, row) => sum + Number(row.paidCount || 0), 0)
        }
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.pay-orders-page .filter-form { margin-bottom: 12px; }
.pay-orders-page .stats-row { display: flex; flex-wrap: wrap; gap: 12px; margin: 0 0 16px; }
.pay-orders-page .stat-card {
  min-width: 150px; padding: 12px 14px; border-radius: 12px;
  background: #f8fafc; border: 1px solid #e2e8f0;
}
.pay-orders-page .stat-card.stat-total { background: #ecfeff; border-color: #99f6e4; }
.pay-orders-page .stat-label { font-size: 12px; color: #64748b; margin-bottom: 6px; }
.pay-orders-page .stat-value { font-size: 20px; font-weight: 700; color: #0f172a; line-height: 1.2; }
.pay-orders-page .stat-sub { margin-top: 4px; font-size: 12px; color: #94a3b8; }
.pay-orders-page .pager-wrap { margin-top: 16px; text-align: right; }
.card-header .hint { margin-left: 12px; font-size: 12px; color: #94a3b8; font-weight: normal; }
.refund-amount { color: #ef4444; font-weight: 600; }
.refund-btn { color: #ef4444; padding: 0; }
.refund-expired { color: #94a3b8; font-size: 12px; white-space: nowrap; }
.text-muted { color: #94a3b8; }
.refund-dialog-body { padding-top: 4px; }
.refund-summary {
  margin-bottom: 16px; padding: 14px 16px; border-radius: 10px;
  background: #fef2f2; border: 1px solid #fecaca;
}
.refund-summary-row {
  display: flex; align-items: flex-start; gap: 12px;
  padding-bottom: 10px; margin-bottom: 10px; border-bottom: 1px dashed #fecaca;
}
.refund-summary-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px;
}
.refund-summary-item { display: flex; flex-direction: column; gap: 4px; }
.refund-summary-item--full { grid-column: 1 / -1; }
.refund-summary-label { font-size: 12px; color: #991b1b; opacity: 0.85; }
.refund-summary-value { font-size: 14px; color: #7f1d1d; font-weight: 600; }
.refund-summary-value.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; word-break: break-all; }
.refund-summary-value.refund-paid { color: #dc2626; }
.refund-summary-value.refund-max { font-size: 16px; color: #dc2626; }
.refund-history {
  margin-bottom: 16px; padding: 12px 14px; border-radius: 10px;
  background: #f8fafc; border: 1px solid #e2e8f0;
}
.refund-history-title { font-size: 12px; color: #64748b; margin-bottom: 8px; font-weight: 600; }
.refund-history-item {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px;
  padding: 6px 0; font-size: 12px; line-height: 1.5; color: #475569;
  border-top: 1px solid #eef2f7;
}
.refund-history-item:first-of-type { border-top: none; padding-top: 0; }
.refund-history-amount { color: #ef4444; font-weight: 600; }
.refund-history-remark { flex: 1; min-width: 120px; }
.refund-history-time { color: #94a3b8; font-size: 11px; }
.refund-form ::v-deep .el-form-item__label { white-space: nowrap; padding-right: 12px; }
.refund-dialog-footer {
  display: flex; justify-content: flex-end; align-items: center; gap: 10px;
}
.refund-remarks { display: flex; flex-direction: column; gap: 4px; }
.refund-remark-item { font-size: 12px; line-height: 1.5; color: #475569; }
.refund-remark-amount { color: #ef4444; font-weight: 600; margin-right: 6px; }
</style>
